import { motion } from "framer-motion";
import { ArrowUpRight, Mail, Phone, MapPin } from "lucide-react";
import { useHome } from "@/context/HomeContent";
import { fadeUp, staggerParent, viewportOnce } from "@/lib/animations";
import { useBooking } from "@/context/Booking";
import { useWhatsAppHref } from "@/lib/whatsapp";

const Footer = () => {
  const { home } = useHome();
  const { openBooking } = useBooking();
  const waHref = useWhatsAppHref();
  const d = home.footer;
  return (
    <footer id="contact" data-testid="site-footer" className="grain relative overflow-hidden bg-brand-ink text-white">
      <div className="pointer-events-none absolute -left-40 -top-20 h-96 w-96 rounded-full bg-brand-blue/20 blur-[130px]" />
      <div className="pointer-events-none absolute -right-40 bottom-0 h-96 w-96 rounded-full bg-brand-magenta/20 blur-[130px]" />

      <div className="relative mx-auto max-w-[1400px] px-5 pt-24 sm:px-8 sm:pt-32">
        <motion.div variants={staggerParent} initial="hidden" whileInView="show" viewport={viewportOnce} className="max-w-4xl">
          <motion.p variants={fadeUp} className="mb-6 font-heading text-xs font-bold uppercase tracking-[0.22em] text-brand-pink">{d.eyebrow}</motion.p>
          <motion.h2 variants={fadeUp} className="font-heading text-4xl font-semibold leading-[1.02] tracking-tight sm:text-6xl lg:text-[4.5rem]">
            {d.headingLead}{" "}
            <span className="text-brand-pink">{d.headingAccent}</span>
          </motion.h2>
          <motion.div variants={fadeUp} className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
            <a href={waHref} target="_blank" rel="noopener noreferrer" data-testid="footer-cta-email" className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-magenta px-8 py-4 font-body text-sm font-semibold text-white transition-transform duration-300 hover:scale-[1.04]">
              {d.ctaPrimary?.label}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <button type="button" onClick={openBooking} data-testid="footer-cta-call" className="inline-flex items-center justify-center gap-2 rounded-full border border-white/25 px-8 py-4 font-body text-sm font-semibold text-white transition-colors duration-300 hover:bg-white hover:text-brand-ink">
              {d.ctaSecondary?.label}
            </button>
          </motion.div>
        </motion.div>

        <div className="mt-20 grid grid-cols-1 gap-10 border-t border-white/10 pt-12 sm:grid-cols-2 lg:grid-cols-4">
          <div className="lg:col-span-2">
            <p className="font-heading text-xl font-semibold">{d.brandName}</p>
            <p className="mt-3 max-w-sm font-body text-sm leading-relaxed text-white/55">{d.about}</p>
          </div>
          <div>
            <p className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-white/40">{d.exploreTitle}</p>
            <ul className="mt-4 space-y-3">
              {(d.links || []).map((l) => (
                <li key={l.href}><a href={l.href} data-testid={`footer-link-${(l.href || "").slice(1)}`} className="font-body text-sm text-white/70 transition-colors duration-300 hover:text-brand-pink">{l.label}</a></li>
              ))}
            </ul>
          </div>
          <div>
            <p className="font-heading text-xs font-bold uppercase tracking-[0.2em] text-white/40">{d.reachTitle}</p>
            <ul className="mt-4 space-y-3 font-body text-sm text-white/70">
              <li className="flex items-center gap-2"><Mail className="h-4 w-4 text-brand-pink" /> {d.email}</li>
              <li className="flex items-center gap-2"><Phone className="h-4 w-4 text-brand-pink" /> {d.phone}</li>
              <li className="flex items-center gap-2"><MapPin className="h-4 w-4 text-brand-pink" /> {d.location}</li>
            </ul>
          </div>
        </div>
      </div>

      <div className="relative mt-16 overflow-hidden border-t border-white/10">
        <h2 className="select-none whitespace-nowrap text-center font-heading text-[10vw] font-extrabold leading-none tracking-tighter text-white/[0.05]">{(d.wordmark || d.brandName)?.toUpperCase()}</h2>
      </div>

      <div className="relative mx-auto flex max-w-[1400px] flex-col items-center justify-between gap-3 px-5 pb-10 sm:flex-row sm:px-8">
        <p className="font-body text-xs text-white/40">© {new Date().getFullYear()} {d.brandName}. All rights reserved.</p>
        <p className="font-body text-xs text-white/40">{d.tagline}</p>
      </div>
    </footer>
  );
};

export default Footer;
